import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { adminAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Loader2, Users, UserCheck, UserX, ShieldOff, MailPlus, Download,
  RefreshCw, Search, ChevronLeft, ChevronRight, Trash2, History,
  Tag, Clock, AlertTriangle, CheckCircle, ArrowUpRight, Database,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  subscribed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  unsubscribed: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  suppressed: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
};

const STATUS_ICONS = {
  subscribed: CheckCircle,
  unsubscribed: UserX,
  suppressed: ShieldOff,
};

const StatCard = ({ label, value, icon: Icon, iconColor = 'text-muted-foreground', testId }) => (
  <Card data-testid={testId}>
    <CardContent className="pt-5 pb-4 px-4">
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide truncate">{label}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
        </div>
        <Icon className={`h-7 w-7 shrink-0 ${iconColor}`} />
      </div>
    </CardContent>
  </Card>
);

const formatDate = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatDateTime = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
};

export const AdminMarketing = () => {
  const { adminRole } = useOutletContext();
  const isPrimaryAdmin = adminRole === 'primary';
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [backfilling, setBackfilling] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactEvents, setContactEvents] = useState([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', name: '', tags: '' });
  const [addSubmitting, setAddSubmitting] = useState(false);

  const PAGE_SIZE = 25;

  const loadStats = useCallback(async () => {
    try {
      const res = await adminAPI.getMarketingStats();
      setStats(res.data);
    } catch { /* ignore */ }
  }, []);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (sourceFilter !== 'all') params.source_type = sourceFilter;
      const res = await adminAPI.getMarketingContacts(params);
      setContacts(res.data.contacts);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch { toast.error('Failed to load contacts'); }
    setLoading(false);
  }, [page, search, statusFilter, sourceFilter]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadContacts(); }, [loadContacts]);

  const handleBackfill = async () => {
    setBackfilling(true);
    try {
      const res = await adminAPI.backfillMarketingContacts();
      const s = res.data.stats;
      toast.success(`Backfill complete: ${s.new} new, ${s.skipped_existing} existing, ${s.skipped_suppressed} suppressed`);
      loadStats();
      loadContacts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Backfill failed');
    }
    setBackfilling(false);
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/marketing/contacts/export${statusFilter !== 'all' ? `?status=${statusFilter}` : ''}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `marketing_contacts_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success('CSV downloaded');
    } catch { toast.error('Export failed'); }
  };

  const openDetail = async (contact) => {
    setSelectedContact(contact);
    setDetailOpen(true);
    setEventsLoading(true);
    try {
      const res = await adminAPI.getMarketingContactEvents(contact.id);
      setContactEvents(res.data.events);
    } catch { setContactEvents([]); }
    setEventsLoading(false);
  };

  const handleStatusChange = async (contactId, newStatus) => {
    try {
      const res = await adminAPI.updateMarketingContact(contactId, { subscription_status: newStatus });
      toast.success(`Status changed to ${newStatus}`);
      setSelectedContact(res.data);
      loadContacts();
      loadStats();
      // Reload events
      const evRes = await adminAPI.getMarketingContactEvents(contactId);
      setContactEvents(evRes.data.events);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const handleComplianceDelete = async (contact) => {
    try {
      await adminAPI.deleteMarketingContact(contact.id);
      toast.success(`${contact.email_normalized} permanently deleted & suppressed`);
      setDeleteConfirm(null);
      setDetailOpen(false);
      loadContacts();
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleAddContact = async () => {
    if (!addForm.email.trim()) { toast.error('Email is required'); return; }
    setAddSubmitting(true);
    try {
      const tags = addForm.tags ? addForm.tags.split(',').map(t => t.trim()).filter(Boolean) : [];
      await adminAPI.addMarketingContact({
        email: addForm.email.trim(),
        name: addForm.name.trim() || null,
        tags: tags.length ? tags : null,
      });
      toast.success('Contact added');
      setAddOpen(false);
      setAddForm({ email: '', name: '', tags: '' });
      loadContacts();
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Add failed');
    }
    setAddSubmitting(false);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    loadContacts();
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="admin-marketing-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl font-bold tracking-tight" data-testid="marketing-page-title">Marketing Contacts</h1>
          <p className="text-sm text-muted-foreground mt-0.5">V1 &mdash; Contact storage, deduplication & compliance</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isPrimaryAdmin && <Button
            variant="outline"
            size="sm"
            onClick={handleBackfill}
            disabled={backfilling}
            data-testid="marketing-backfill-btn"
          >
            {backfilling ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Database className="w-4 h-4 mr-1.5" />}
            {backfilling ? 'Backfilling...' : 'Backfill Users'}
          </Button>}
          <Button variant="outline" size="sm" onClick={handleExport} data-testid="marketing-export-btn">
            <Download className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
          {isPrimaryAdmin && <Button size="sm" onClick={() => setAddOpen(true)} data-testid="marketing-add-btn">
            <MailPlus className="w-4 h-4 mr-1.5" /> Add Contact
          </Button>}
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="marketing-stats-grid">
          <StatCard label="Total Contacts" value={stats.total} icon={Users} iconColor="text-blue-500" testId="stat-total" />
          <StatCard label="Subscribed" value={stats.subscribed} icon={UserCheck} iconColor="text-emerald-500" testId="stat-subscribed" />
          <StatCard label="Unsubscribed" value={stats.unsubscribed} icon={UserX} iconColor="text-amber-500" testId="stat-unsubscribed" />
          <StatCard label="Suppressed" value={stats.suppressed} icon={ShieldOff} iconColor="text-red-500" testId="stat-suppressed" />
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
          <StatCard label="Added (7 days)" value={stats.recent_7d} icon={Clock} iconColor="text-violet-500" testId="stat-recent" />
          <StatCard label="Suppressions" value={stats.total_suppressions} icon={ShieldOff} iconColor="text-rose-500" testId="stat-suppressions" />
          <Card data-testid="stat-sources">
            <CardContent className="pt-5 pb-4 px-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">By Source</p>
              <div className="space-y-1">
                {stats.by_source && Object.entries(stats.by_source).map(([src, count]) => (
                  <div key={src} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{src.replace(/_/g, ' ')}</span>
                    <span className="font-semibold">{count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="mb-4">
        <CardContent className="pt-4 pb-3 px-4">
          <form onSubmit={handleSearchSubmit} className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search email or name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="marketing-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-full sm:w-40" data-testid="marketing-status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="subscribed">Subscribed</SelectItem>
                <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
                <SelectItem value="suppressed">Suppressed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sourceFilter} onValueChange={(v) => { setSourceFilter(v); setPage(1); }}>
              <SelectTrigger className="w-full sm:w-40" data-testid="marketing-source-filter">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="user_backfill">User Backfill</SelectItem>
                <SelectItem value="manual_add">Manual Add</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" variant="secondary" size="sm" className="shrink-0" data-testid="marketing-search-btn">
              <Search className="w-4 h-4 mr-1" /> Search
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Contacts Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="marketing-contacts-table">
            <thead>
              <tr className="border-b bg-muted/40">
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Email</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden md:table-cell">Name</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground">Status</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden lg:table-cell">Source</th>
                <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden lg:table-cell">First Seen</th>
                <th className="text-right px-4 py-3 font-medium text-muted-foreground w-20">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                  </td>
                </tr>
              ) : contacts.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-muted-foreground">
                    No contacts found
                  </td>
                </tr>
              ) : contacts.map((c) => {
                const SIcon = STATUS_ICONS[c.subscription_status] || UserCheck;
                return (
                  <tr
                    key={c.id}
                    className="border-b last:border-0 hover:bg-muted/30 transition-colors cursor-pointer"
                    onClick={() => openDetail(c)}
                    data-testid={`contact-row-${c.email_normalized}`}
                  >
                    <td className="px-4 py-3 font-medium truncate max-w-[200px]">{c.email_normalized}</td>
                    <td className="px-4 py-3 text-muted-foreground hidden md:table-cell truncate max-w-[150px]">{c.name || '-'}</td>
                    <td className="px-4 py-3">
                      <Badge variant="secondary" className={`text-xs ${STATUS_COLORS[c.subscription_status] || ''}`}>
                        <SIcon className="w-3 h-3 mr-1" />
                        {c.subscription_status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground hidden lg:table-cell text-xs">{c.source_type?.replace(/_/g, ' ') || '-'}</td>
                    <td className="px-4 py-3 text-muted-foreground hidden lg:table-cell text-xs">{formatDate(c.first_seen_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`contact-detail-btn-${c.email_normalized}`}>
                        <ArrowUpRight className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t" data-testid="marketing-pagination">
            <p className="text-xs text-muted-foreground">
              Page {page} of {totalPages} ({total} contacts)
            </p>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="pagination-prev">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="pagination-next">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Contact Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="contact-detail-dialog">
          {selectedContact && (
            <>
              <DialogHeader>
                <DialogTitle className="text-lg">{selectedContact.email_normalized}</DialogTitle>
                <DialogDescription>
                  {selectedContact.name || 'No name'} &middot; {selectedContact.source_type?.replace(/_/g, ' ')}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 mt-2">
                {/* Status */}
                <div className="flex items-center gap-3">
                  <Badge variant="secondary" className={`${STATUS_COLORS[selectedContact.subscription_status] || ''}`}>
                    {selectedContact.subscription_status}
                  </Badge>
                  {selectedContact.consent_status && (
                    <span className="text-xs text-muted-foreground">Consent: {selectedContact.consent_status}</span>
                  )}
                </div>

                {/* Tags */}
                {selectedContact.tags?.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Tag className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    {selectedContact.tags.map(t => (
                      <Badge key={t} variant="outline" className="text-xs">{t}</Badge>
                    ))}
                  </div>
                )}

                {/* Metadata */}
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-muted-foreground">First seen</span>
                    <p className="font-medium">{formatDateTime(selectedContact.first_seen_at)}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Last seen</span>
                    <p className="font-medium">{formatDateTime(selectedContact.last_seen_at)}</p>
                  </div>
                  {selectedContact.unsubscribed_at && (
                    <div>
                      <span className="text-muted-foreground">Unsubscribed</span>
                      <p className="font-medium">{formatDateTime(selectedContact.unsubscribed_at)}</p>
                    </div>
                  )}
                  {selectedContact.suppression_reason && (
                    <div>
                      <span className="text-muted-foreground">Suppression reason</span>
                      <p className="font-medium">{selectedContact.suppression_reason}</p>
                    </div>
                  )}
                </div>

                {/* Status actions */}
                {isPrimaryAdmin && <div className="flex gap-2 flex-wrap">
                  {selectedContact.subscription_status !== 'subscribed' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStatusChange(selectedContact.id, 'subscribed')}
                      data-testid="contact-resubscribe-btn"
                    >
                      <UserCheck className="w-3.5 h-3.5 mr-1" /> Resubscribe
                    </Button>
                  )}
                  {selectedContact.subscription_status !== 'unsubscribed' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStatusChange(selectedContact.id, 'unsubscribed')}
                      data-testid="contact-unsubscribe-btn"
                    >
                      <UserX className="w-3.5 h-3.5 mr-1" /> Unsubscribe
                    </Button>
                  )}
                  {selectedContact.subscription_status !== 'suppressed' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStatusChange(selectedContact.id, 'suppressed')}
                      data-testid="contact-suppress-btn"
                    >
                      <ShieldOff className="w-3.5 h-3.5 mr-1" /> Suppress
                    </Button>
                  )}
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setDeleteConfirm(selectedContact)}
                    data-testid="contact-delete-btn"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Compliance Delete
                  </Button>
                </div>}

                {/* Event History */}
                <div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <History className="w-4 h-4 text-muted-foreground" />
                    <h3 className="text-sm font-semibold">Event History</h3>
                  </div>
                  {eventsLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                  ) : contactEvents.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No events recorded</p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto" data-testid="contact-events-list">
                      {contactEvents.map((ev) => (
                        <div key={ev.id} className="flex items-start gap-2 text-xs border-l-2 border-muted pl-3 py-1">
                          <div className="flex-1 min-w-0">
                            <span className="font-medium">{ev.event_type.replace(/_/g, ' ')}</span>
                            <span className="text-muted-foreground ml-1.5">by {ev.actor_name}</span>
                            <p className="text-muted-foreground">{formatDateTime(ev.created_at)}</p>
                            {ev.details && (
                              <pre className="text-[10px] text-muted-foreground mt-0.5 whitespace-pre-wrap break-all">
                                {JSON.stringify(ev.details, null, 1)}
                              </pre>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm" data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" /> Compliance Delete
            </DialogTitle>
            <DialogDescription>
              This will permanently delete <strong>{deleteConfirm?.email_normalized}</strong> and add the email to the permanent suppression list. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => handleComplianceDelete(deleteConfirm)}
              data-testid="delete-confirm-btn"
            >
              Delete & Suppress
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Contact Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-sm" data-testid="add-contact-dialog">
          <DialogHeader>
            <DialogTitle>Add Contact</DialogTitle>
            <DialogDescription>Manually add a marketing contact.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Email address"
              value={addForm.email}
              onChange={(e) => setAddForm(f => ({ ...f, email: e.target.value }))}
              data-testid="add-contact-email"
            />
            <Input
              placeholder="Name (optional)"
              value={addForm.name}
              onChange={(e) => setAddForm(f => ({ ...f, name: e.target.value }))}
              data-testid="add-contact-name"
            />
            <Input
              placeholder="Tags (comma-separated, optional)"
              value={addForm.tags}
              onChange={(e) => setAddForm(f => ({ ...f, tags: e.target.value }))}
              data-testid="add-contact-tags"
            />
          </div>
          <DialogFooter className="gap-2 mt-2">
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={handleAddContact} disabled={addSubmitting} data-testid="add-contact-submit">
              {addSubmitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <MailPlus className="w-4 h-4 mr-1" />}
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMarketing;
